import React, { useState, useEffect, useRef, useMemo } from "react";
import { useDispatch, useSelector } from "react-redux";
import { createAdvertisementPlay } from "../../store/actions/adversitementsActions";
import { motion, AnimatePresence } from "framer-motion";
import { useTranslation } from "react-i18next";

const AdvertisementModal = ({ onClose }) => {
  const dispatch = useDispatch();
  const { t } = useTranslation();
  const closeLabel = t("advertisement.close");
  const { advertisements } = useSelector((state) => state.advertisements);
  const [currentAdIndex, setCurrentAdIndex] = useState(0);
  const [progress, setProgress] = useState(0);
  const [playCounts, setPlayCounts] = useState({});
  const videoRef = useRef(null);
  const deviceToken = localStorage.getItem("deviceToken");

  // Сортируем рекламы по полю order (ascending). Если order отсутствует — в конец.
  const sortedAdvertisements = useMemo(() => {
    if (!advertisements?.advertisements?.length) return [];
    return [...advertisements.advertisements].sort((a, b) => {
      const orderA = a.order ?? Infinity;
      const orderB = b.order ?? Infinity;
      return orderA - orderB;
    });
  }, [advertisements]);

  // Текущая реклама
  const currentAd = sortedAdvertisements[currentAdIndex];

  // Количество реклам и требуемое количество проигрываний
  const adCount = sortedAdvertisements.length || 0;
  const playsPerAd = adCount === 2 ? 5 : adCount >= 3 ? 4 : 3;
  const totalRequiredPlays = adCount * playsPerAd;

  // Варианты анимации видео
  const videoVariants = {
    initial: { opacity: 0, scale: 0.98 },
    animate: { opacity: 1, scale: 1, transition: { duration: 0.99, ease: "easeInOut" } },
  };

  // Прогресс видео
  const handleTimeUpdate = () => {
    if (videoRef.current) {
      const currentTime = videoRef.current.currentTime;
      const duration = videoRef.current.duration;
      if (duration) {
        setProgress((currentTime / duration) * 100);
      }
    }
  };

  // Окончание видео
  const handleVideoEnd = () => {
    if (currentAd && deviceToken) {
      dispatch(
        createAdvertisementPlay({
          advertisementId: currentAd.id,
          deviceToken,
        })
      );
    }

    // Обновляем счётчик проигрываний
    setPlayCounts((prev) => ({
      ...prev,
      [currentAd.id]: (prev[currentAd.id] || 0) + 1,
    }));

    // Общее количество проигрываний (текущее состояние playCounts ещё старое, поэтому +1 вручную)
    const totalPlays =
      Object.values(playCounts).reduce((sum, count) => sum + count, 0) + 1;

    // Если достигнуто требуемое количество — закрываем модал и сбрасываем счётчики
    if (totalPlays >= totalRequiredPlays) {
      if (videoRef.current) {
        videoRef.current.pause();
      }
      setPlayCounts(
        sortedAdvertisements.reduce((acc, ad) => {
          acc[ad.id] = 0;
          return acc;
        }, {})
      );
      onClose();
      return;
    }

    // Переходим к следующей рекламе
    setProgress(0);
    setCurrentAdIndex((prevIndex) =>
      prevIndex + 1 < sortedAdvertisements.length ? prevIndex + 1 : 0
    );
  };

  // Автозапуск видео при смене рекламы
  useEffect(() => {
    if (videoRef.current && currentAd) {
      videoRef.current.load();
      videoRef.current.play().catch((error) => {
        console.error("Error playing video:", error);
      });
    }
  }, [currentAdIndex, currentAd]);

  // Инициализация/сброс счётчиков и индекса при изменении списка реклам
  useEffect(() => {
    if (sortedAdvertisements.length > 0) {
      const initialPlayCounts = sortedAdvertisements.reduce((acc, ad) => {
        acc[ad.id] = 0;
        return acc;
      }, {});
      setPlayCounts(initialPlayCounts);
      setCurrentAdIndex(0);
      setProgress(0);
    }
  }, [sortedAdvertisements]);

  // Если нет реклам — ничего не рендерим
  if (sortedAdvertisements.length === 0) {
    return null;
  }

  return (
    <motion.div
      className="fixed inset-0 flex items-center justify-center z-30 bg-black bg-opacity-50 backdrop-blur-md flex-col"
      initial={{ opacity: 0, scale: 0.95 }}
      animate={{ opacity: 1, scale: 1 }}
      exit={{ opacity: 0, scale: 0.95 }}
      transition={{ duration: 0.96, ease: "easeInOut" }}
      style={{ width: "100vw", height: "100vh" }}
    >
      <div className="relative w-[100vw] h-[100vh] bg-transparent flex flex-col items-center justify-center">
        <AnimatePresence>
          <motion.video
            key={currentAd.id}
            ref={videoRef}
            className="w-full h-[100vh] object-contain rounded-[2vw]"
            variants={videoVariants}
            initial="initial"
            animate="animate"
            onTimeUpdate={handleTimeUpdate}
            onEnded={handleVideoEnd}
            autoPlay
            muted
          >
            <source
              // ad_path из /advertisements/create приходит без ведущего слэша
              // (admin_banners_crud.py кладёт "media/videos/…") — без нормализации
              // склеивается в "…/apimedia/videos/…" и видео не грузится.
              src={`${process.env.REACT_APP_BASEURL}/${currentAd.ad_path.replace(/^\/+/, "")}`}
              type="video/mp4"
            />
            Your browser does not support the video tag.
          </motion.video>
        </AnimatePresence>
      </div>

      <motion.button
        onClick={() => {
          if (videoRef.current) {
            videoRef.current.pause();
          }
          onClose();
        }}
        className="absolute left-1/2 bottom-[3vh] px-[3vw] py-[1.6vh] bg-white/90 text-[#12161F] font-bold rounded-full text-[2vh] shadow-[0_1vh_3vh_rgba(18,22,31,0.28)] transition-opacity"
        /* Раньше центрировался случайно, наследуя позицию от родителя (flex
           items-center justify-center) — без left/translate это ломается при
           любой правке разметки вокруг. x через style (не Tailwind-класс) —
           framer-motion пишет transform сам через whileTap (см. гоча в
           CLAUDE.md про конфликт framer-motion и Tailwind transform-классов). */
        style={{ x: "-50%" }}
        whileTap={{ scale: 0.95 }}
      >
        {closeLabel}
      </motion.button>
    </motion.div>
  );
};

export default AdvertisementModal;